import {
    ActivityHistoryEntryDetails,
    ActivityType,
    NotificationConfigDetails,
    RiskProfileConfigDetails,
} from '@evidentid/tprm-portal-lib/models/activity-history';

import { escapeHtml } from '@/modules/entity-events/utils/escapeHtml';

const NOTIFICATION_TYPE_LABELS: Record<string, string> = {
    NEW: 'New',
    NON_COMPLIANT: 'Non-Compliant',
    EXPIRING: 'Expiring',
    NEW_ENTITY_BODY: 'New Entity',
    COMPLIANT: 'Compliant',
};

function bold(text: string): string {
    return `<strong>${escapeHtml(text)}</strong>`;
}

function notificationLabel(details: NotificationConfigDetails): string {
    return bold(NOTIFICATION_TYPE_LABELS[details.notificationType] || details.notificationType);
}

function enablementSummary(details: NotificationConfigDetails): string {
    // initEnabled and remindersEnabled are computed independently on the backend, so a single save that
    // flips both (e.g. toggling initial sends and reminders together) populates both flags on one event.
    const clauses: string[] = [];
    if (details.initEnabled != null) {
        const verb = details.initEnabled ? 'enabled' : 'disabled';
        clauses.push(`Notifications ${verb}`);
    }
    if (details.remindersEnabled != null) {
        const verb = details.remindersEnabled ? 'enabled' : 'disabled';
        clauses.push(`Reminders ${verb}`);
    }
    if (clauses.length === 0) {
        return `Notification settings changed for ${notificationLabel(details)} notifications`;
    }
    return `${clauses.join(' and ')} for ${notificationLabel(details)} notifications`;
}

/**
 * Human summary for a global (non-entity) activity event.
 *
 * Returns null for any entity event type, so the table row can call this first and fall through to its own
 * switch on a miss. Returns the raw activity type when the details payload is absent, matching how the row
 * degrades for unrecognised entity events.
 */
export function formatGlobalEventSummary(
    activityType: ActivityType,
    details: ActivityHistoryEntryDetails | null,
): string | null {
    switch (activityType) {
        case ActivityType.RISK_PROFILE_CREATED:
        case ActivityType.RISK_PROFILE_UPDATED:
        case ActivityType.RISK_PROFILE_DELETED:
        case ActivityType.RISK_PROFILE_CRITERIA_CHANGED: {
            const profile = details as RiskProfileConfigDetails | null;
            if (!profile) {
                return activityType;
            }
            const name = bold(profile.riskProfileName);
            if (activityType === ActivityType.RISK_PROFILE_CREATED) {
                return `Risk profile ${name} was created`;
            }
            if (activityType === ActivityType.RISK_PROFILE_DELETED) {
                return `Risk profile ${name} was deleted`;
            }
            if (activityType === ActivityType.RISK_PROFILE_CRITERIA_CHANGED) {
                return `Criteria changed on risk profile ${name}`;
            }
            const changed = profile.changedFields || [];
            return changed.length > 0
                ? `Risk profile ${name} was updated: ${changed.map(escapeHtml).join(', ')}`
                : `Risk profile ${name} was updated`;
        }
        case ActivityType.NOTIFICATION_ENABLEMENT_CHANGED: {
            const notification = details as NotificationConfigDetails | null;
            return notification ? enablementSummary(notification) : activityType;
        }
        case ActivityType.NOTIFICATION_CONTENT_CHANGED: {
            const notification = details as NotificationConfigDetails | null;
            if (!notification) {
                return activityType;
            }
            const subject = notification.recipient === 'producer' ? 'Producer email content' : 'Email content';
            return `${subject} changed for ${notificationLabel(notification)} notifications`;
        }
        case ActivityType.BRANDING_CHANGED:
            return 'Branding settings were changed';
        case ActivityType.RP_PROFILE_CHANGED:
            return 'Profile settings were changed';
        case ActivityType.CADENCE_CHANGED:
            return 'Reminder cadence was changed';
        case ActivityType.WEBHOOK_CREATED:
            return 'A webhook was created';
        case ActivityType.WEBHOOK_UPDATED:
            return 'A webhook was updated';
        case ActivityType.WEBHOOK_DELETED:
            return 'A webhook was deleted';
        case ActivityType.SSO_SETTINGS_CHANGED:
            return 'SSO settings were changed';
        default:
            return null;
    }
}
